// Prompt library and prompting guide for the Tracey page.
//
// Content comes from "The Complete Guide to Tracey" (the Prompt Cookbook and the
// prompting guide), so it is versioned with the UI rather than held in the
// database. Each prompt is tagged with the agent it is written for.

const SPRINT_CATEGORIES = [
  ['Tender and contract search', [
    'Find open tenders for cyber security in NSW closing this month.',
    'Show contracts over $1M awarded by the Department of Defence this year.',
    'Search for cloud services tenders across all federal agencies.',
    'List recent ICT contracts in Victoria above $500K.',
    'Find managed services tenders published in the last 30 days.',
  ]],
  ['Supplier intelligence', [
    'Who are the top suppliers to the Department of Health?',
    'Show recent contracts won by Deloitte across government.',
    'Which suppliers are new entrants in NSW transport?',
    'What has ServiceNow been awarded in the last 12 months?',
    'Compare two suppliers by total contract value and buyers served.',
  ]],
  ['Buyer and department activity', [
    'What has Services Australia been buying recently?',
    'Show procurement activity for Transport for NSW this quarter.',
    'Which departments are most active in cloud procurement?',
    'Summarise recent spending by the Australian Taxation Office.',
  ]],
  ['Market and category insights', [
    'Give me a market overview of the cyber security category.',
    'How much did the federal government spend on consulting last year?',
    'What are the trends in AI and data analytics procurement?',
    'Show the size and growth of the facilities management market.',
  ]],
  ['Leaderboards and rankings', [
    'Top 10 suppliers by contract value in Defence.',
    'Rank buyers by ICT spend in NSW.',
    'Which categories have the highest total awarded value this year?',
    'Show the most active suppliers in health technology.',
  ]],
  ['Quick opportunity lookups', [
    'Any new tenders matching managed security services?',
    'Show upcoming opportunities in health technology.',
    'Find contracts likely to be retendered soon in my category.',
    'What closed recently that I may have missed in cloud services?',
  ]],
];

const ULTRA_CATEGORIES = [
  ['Account planning and briefs', [
    'Build an account brief for the Department of Health.',
    'Prepare me for a meeting with Transport for NSW, including incumbents and opportunities.',
    'Give me a full account plan for Services Australia.',
    'What should I know before pursuing the Department of Education?',
  ]],
  ['Opportunity discovery', [
    'Which agencies received new budget for cyber security but have not yet gone to market?',
    'Where is there unspent or undeployed budget in ICT?',
    'Show me new money in the 2026-27 Budget for facilities management.',
    'Which new Budget measures map to my products and services?',
  ]],
  ['Budget and fiscal intelligence', [
    'What did the federal Budget allocate to Defence ICT this year?',
    'Summarise MYEFO changes affecting health spending.',
    'Show forward estimates for infrastructure over the next four years.',
    'Where were the largest savings or cuts in the latest Budget?',
  ]],
  ['Strategy versus procurement', [
    'Compare the Defence corporate plan priorities to its actual procurement.',
    'Where is Health stating digital transformation but still buying from legacy integrators?',
    "Show where an agency's spend diverges from its strategic priorities.",
  ]],
  ['Compliance and policy', [
    'What is the IPP compliance position for Defence?',
    'Which agencies are likely to prioritise Indigenous suppliers before 30 June?',
    'Explain the Commonwealth Procurement Rules thresholds relevant to my category.',
  ]],
  ['Forecasting and timing', [
    'When is Health likely to procure in digital health over the next 12 months?',
    'Which agencies face end of financial year spending pressure in ICT?',
    'Model the likely procurement windows for infrastructure this year.',
  ]],
  ['Contract expiry and retendering', [
    'Which contracts at the Department of Education are expiring in the next six months?',
    'Show retendering signals in NSW transport.',
    'Which departments have the highest ratio of expiring contracts to new funding?',
  ]],
  ['Competitor and incumbent intelligence', [
    'Who are the incumbent suppliers at Services Australia and how concentrated are they?',
    "Analyse my competitors' activity across my target accounts.",
    'Where are incumbents vulnerable at my target agencies?',
  ]],
  ['Cluster management', [
    'Create a cluster of my main competitors and track their wins.',
    'Add these suppliers to my Major Tech Providers cluster.',
    'Show contract activity across my competitor cluster this quarter.',
  ]],
  ['Saved searches and pipelines', [
    'Save this search for cyber tenders over $1M and alert me weekly.',
    'Create a pipeline for Defence ICT opportunities.',
    'Add the most promising opportunities from this result to a pipeline.',
  ]],
  ['AIIA policy intelligence', [
    'What is the AIIA position on the Digital Seller Underperformance Policy?',
    'What does the AIIA say about local content rules and SME access?',
    'Summarise the AIIA view on automated decision making obligations.',
  ]],
  ['Victorian Government intelligence', [
    'What does the Silver Review recommend for the Department of Health?',
    'Connect the Victorian Budget transport funding to expiring DTP contracts and their incumbents.',
    'Give me a Victorian Government account brief for the Department of Education.',
  ]],
  ['Reports and PDF generation', [
    'Generate a PDF opportunity report for NSW health technology.',
    'Create a one page account brief PDF for Defence.',
    'Turn this analysis into a shareable PDF report.',
  ]],
  ['Cross comparison across all layers', [
    'Cross reference new Budget funding with current contract activity in cyber.',
    'Combine strategy, budget and contract data to find my best three opportunities.',
    'Size the total addressable opportunity in health technology and name the incumbents.',
  ]],
];

function toPrompts(agent, categories) {
  return categories.flatMap(([category, prompts], c) =>
    prompts.map((prompt, i) => ({
      id: `${agent.toLowerCase()}-${c + 1}-${i + 1}`,
      agent,
      category,
      prompt,
    })));
}

export const PROMPT_AGENTS = ['All', 'Sprint', 'Ultra'];

export const PROMPT_LIBRARY = [
  ...toPrompts('Sprint', SPRINT_CATEGORIES),
  ...toPrompts('Ultra', ULTRA_CATEGORIES),
];

export const PROMPTING_GUIDE = {
  formula: 'Action + subject and filters + your goal + output format',
  formulaExample:
    '"Show me (action) ICT contracts over $1M awarded by the Department of Health in the last 12 months '
    + '(subject and filters), so I can find accounts to target (goal), as a table sorted by value (output)."',
  agents: [
    {
      name: 'Tracey Sprint',
      when: 'You want fast, focused answers: tender and contract lookups, supplier and buyer activity, '
        + 'market and leaderboard insights, and quick opportunity checks. Lower cost.',
    },
    {
      name: 'Tracey Ultra',
      when: 'You want deep, strategic intelligence that combines contracts with Budget, MYEFO, policy and '
        + 'plans: account briefs, opportunity strategy, forecasting, and PDF reports. Higher cost.',
    },
  ],
  habits: [
    {
      title: 'Set up your profile first',
      body: 'Tracey personalises answers using your company, role, services, target accounts and competitors. '
        + 'With a complete profile, prompts like "find opportunities for me" become far more useful. If you have '
        + 'not set it up, ask Tracey to show you the setup video.',
    },
    {
      title: 'Pick the right agent for the job',
      body: 'Use Tracey Sprint for fast, focused lookups (tenders, suppliers, buyers, market snapshots). Use Tracey '
        + 'Ultra for deeper work that combines contracts with Budget, MYEFO, policy and plans (account briefs, '
        + 'opportunity strategy, forecasting, reports).',
    },
    {
      title: 'Be specific about the details',
      body: 'The more of these you include, the tighter the answer. Add only the ones that matter: who (buyer or '
        + 'agency), what (category or keywords), supplier or competitor, where (state or jurisdiction), how much '
        + '(value range), when (dates or expiry window), how (procurement method or panel), and output (list, '
        + 'table, brief or PDF).',
    },
    {
      title: 'State the outcome you want',
      body: 'Tell Tracey what the answer is for. "Give me a list", "compare these two", "build an account brief", '
        + '"create a pipeline", or "generate a PDF report" all lead to different, better shaped responses than a '
        + 'vague question.',
    },
    {
      title: 'One clear task per prompt',
      body: 'Keep each prompt focused on a single job. For a bigger goal, either break it into steps or ask Tracey '
        + 'to run the whole play, for example: "Do a full account plan for Defence: strategy, funding, incumbents, '
        + 'expiries and next steps."',
    },
    {
      title: 'Give context and your goal',
      body: 'Share the why. "I sell managed security services and need to hit a $40M quota this year" lets Tracey '
        + 'rank and recommend, not just retrieve. Mention constraints too, such as territory, budget range or '
        + 'sector focus.',
    },
    {
      title: 'Refine in the same chat',
      body: 'Tracey remembers the conversation, so build on it. Start broad, then narrow: "Now filter to NSW only", '
        + '"Just the top five by value", "Focus on contracts expiring before June", "Turn that into a PDF".',
    },
    {
      title: 'Ask for the "so what", not just data',
      body: 'Request signals and next steps: "What does this mean for me?", "Where are the incumbents vulnerable?", '
        + '"Which of these should I prioritise and why?". Tracey is built to advise, not only to list.',
    },
    {
      title: 'Use Ultra for cross layer questions',
      body: 'Ultra is strongest when you combine sources: "Cross reference new Budget funding with current '
        + 'contract activity in cyber", or "Connect the Silver Review reforms and Victorian Budget to expiring '
        + 'contracts at the Department of Health".',
    },
    {
      title: 'Ask what Tracey can access, and for sources',
      body: 'If you are unsure of scope, ask "what can you analyse?" or "what data sources do you have?". For '
        + 'Budget, policy and Victorian questions, ask Tracey to cite its sources so you can trust and share the '
        + 'answer.',
    },
    {
      title: 'Turn insights into actions',
      body: 'Phrase actions as commands: "Save this search and alert me weekly", "Create a cluster of these '
        + 'competitors", "Add these to my Defence pipeline", "Generate a one page PDF brief". Tracey can create '
        + 'and manage these for you.',
    },
    {
      title: 'If an answer misses, say what to change',
      body: 'Tell Tracey how to adjust rather than starting over: "That is too broad, limit to health", "Use expiry '
        + 'dates, not published dates", "Exclude construction", "Show dollar values". Small corrections steer it '
        + 'quickly.',
    },
  ],
  examples: [
    {
      weak: 'Show me health contracts.',
      strong: 'Show contracts awarded by the Victorian Department of Health for ICT services over $1M in the last '
        + '12 months, sorted by value.',
    },
    {
      weak: 'Who are my competitors?',
      strong: "Analyse my competitors' contract wins across my target accounts over the past year and highlight "
        + 'where they are gaining share.',
    },
    {
      weak: 'Budget stuff for Defence.',
      strong: 'Summarise new Budget measures for Defence ICT in 2026-27 and flag which ones have not yet gone to '
        + 'market.',
    },
    {
      weak: 'Find opportunities.',
      strong: 'Find opportunities that match my services and quota: funded categories at my target accounts with '
        + 'contracts expiring in the next six months.',
    },
    {
      weak: 'Make a report.',
      strong: 'Generate a PDF opportunity report for NSW health technology, including incumbents, expiring '
        + 'contracts and recommended next steps.',
    },
  ],
};
